import { type OrganizationRole } from './constants.js';

/**
 * Fine-grained permissions. Routes declare the permission they require; roles map to sets of
 * permissions. Adding a role later only requires extending ROLE_PERMISSIONS, and an
 * organization membership may carry `customPermissions` that add to the role's set.
 */
export const PERMISSIONS = [
  'organization:read',
  'organization:write',
  'users:read',
  'users:invite',
  'users:write',
  'locations:read',
  'locations:write',
  'locations:delete',
  'screens:read',
  'screens:write',
  'screens:delete',
  'screens:command',
  'media:read',
  'media:write',
  'media:delete',
  'playlists:read',
  'playlists:write',
  'playlists:delete',
  'schedules:read',
  'schedules:write',
  'schedules:delete',
  'analytics:read',
  'notifications:read',
  'notifications:write',
  'billing:read',
  'billing:manage',
  'audit:read',
  'apikeys:read',
  'apikeys:write',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const VIEWER: Permission[] = [
  'organization:read',
  'users:read',
  'locations:read',
  'screens:read',
  'media:read',
  'playlists:read',
  'schedules:read',
  'analytics:read',
  'notifications:read',
];

const CONTENT_MANAGER: Permission[] = [
  ...VIEWER,
  'media:write',
  'media:delete',
  'playlists:write',
  'playlists:delete',
  'schedules:write',
  'schedules:delete',
  'screens:write',
  'screens:command',
  'notifications:write',
];

const ADMIN: Permission[] = [
  ...CONTENT_MANAGER,
  'organization:write',
  'users:invite',
  'users:write',
  'locations:write',
  'locations:delete',
  'screens:delete',
  'billing:read',
  'audit:read',
  'apikeys:read',
  'apikeys:write',
];

const OWNER: Permission[] = [...ADMIN, 'billing:manage'];

export const ROLE_PERMISSIONS: Record<OrganizationRole, readonly Permission[]> = {
  viewer: VIEWER,
  content_manager: CONTENT_MANAGER,
  admin: ADMIN,
  owner: OWNER,
};

export const ROLE_LABELS: Record<OrganizationRole, string> = {
  owner: 'Owner',
  admin: 'Administrator',
  content_manager: 'Content Manager',
  viewer: 'Viewer',
};

/** Roles ordered from most to least privileged; used for "can this user manage that user". */
export const ROLE_RANK: Record<OrganizationRole, number> = {
  owner: 40,
  admin: 30,
  content_manager: 20,
  viewer: 10,
};

export function permissionsForRole(
  role: OrganizationRole,
  customPermissions: readonly string[] = [],
): Set<Permission> {
  const set = new Set<Permission>(ROLE_PERMISSIONS[role]);
  for (const p of customPermissions) {
    if ((PERMISSIONS as readonly string[]).includes(p)) set.add(p as Permission);
  }
  return set;
}

export function hasPermission(
  role: OrganizationRole,
  permission: Permission,
  customPermissions: readonly string[] = [],
): boolean {
  return permissionsForRole(role, customPermissions).has(permission);
}

export function canManageRole(actor: OrganizationRole, target: OrganizationRole): boolean {
  // An actor may only assign/modify roles strictly below their own rank, except owners who
  // may assign any role including owner.
  if (actor === 'owner') return true;
  return ROLE_RANK[actor] > ROLE_RANK[target];
}
